import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseColumnResizeOptions {
  minWidth?: number;
  maxWidth?: number;
}

/**
 * 표 컬럼 헤더를 잡고 끌어 가로폭을 조절하는 동작을 캡슐화한 훅.
 *
 * 사용법:
 *   - `startResize(colKey, currentWidth)` 가 반환한 핸들러를 손잡이 div 의 `onMouseDown` 에 연결.
 *   - 헤더·셀 렌더 시 `draftWidths[colKey] ?? savedWidth` 를 폭으로 적용해 드래그 중 미리보기를 그린다.
 *   - 마우스를 놓는 순간 `onCommit(colKey, finalWidth)` 가 단 한 번 호출 — 영구 저장은 호출자 책임.
 *   - Esc 로 드래그 도중 취소 가능. 컴포넌트 unmount 시 진행 중 드래그는 자동 정리.
 *
 * 드래그 중에는 commit 콜백을 호출하지 않아 자동저장 폭주·리렌더 폭주를 방지한다.
 */
export function useColumnResize<K extends string = string>(
  onCommit: (key: K, width: number) => void,
  { minWidth = 60, maxWidth }: UseColumnResizeOptions = {},
): {
  draftWidths: Partial<Record<K, number>>;
  startResize: (key: K, currentWidth: number) => (e: React.MouseEvent) => void;
} {
  const [draftWidths, setDraftWidths] = useState<Partial<Record<K, number>>>({});
  const draftRef = useRef<Partial<Record<K, number>>>({});
  const teardownRef = useRef<(() => void) | null>(null);

  // 컴포넌트 unmount 시 진행 중인 드래그가 있으면 안전하게 정리(이벤트 리스너/커서 복원).
  useEffect(() => {
    return () => {
      teardownRef.current?.();
    };
  }, []);

  const startResize = useCallback(
    (key: K, currentWidth: number) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // 이미 다른 드래그가 진행 중이면 그것부터 commit 없이 정리.
      teardownRef.current?.();

      const startX = e.clientX;
      const startWidth = currentWidth;
      const clamp = (n: number): number => {
        let v = Math.max(minWidth, n);
        if (maxWidth !== undefined) v = Math.min(maxWidth, v);
        return v;
      };

      const onMove = (ev: MouseEvent): void => {
        const next = clamp(startWidth + (ev.clientX - startX));
        draftRef.current = { ...draftRef.current, [key]: next };
        setDraftWidths(draftRef.current);
      };

      const finish = (commit: boolean): void => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.removeEventListener('keydown', onKey);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        teardownRef.current = null;

        const final = draftRef.current[key];
        const next: Partial<Record<K, number>> = { ...draftRef.current };
        delete next[key];
        draftRef.current = next;
        setDraftWidths(next);

        if (commit && final !== undefined && final !== startWidth) {
          onCommit(key, final);
        }
      };

      const onUp = (): void => finish(true);
      const onKey = (ev: KeyboardEvent): void => {
        if (ev.key === 'Escape') finish(false);
      };

      teardownRef.current = () => finish(false);

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('keydown', onKey);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [maxWidth, minWidth, onCommit],
  );

  return { draftWidths, startResize };
}
